import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from './ui/modal';
import { Card, CardContent } from './ui/card';
import { Shield, Cookie } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface PolicyContentItem {
  type: string;
  description: string;
}

type PolicySectionContent = string | PolicyContentItem[];

interface PolicySection {
  title: string;
  content: PolicySectionContent;
}

interface PolicyDocument {
  title: string;
  subtitle: string;
  sections: PolicySection[];
}

interface PolicyModalProps {
  type: 'privacy' | 'cookie' | 'kvkk';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PolicyModal: React.FC<PolicyModalProps> = ({ type, open, onOpenChange }) => {
  const { language } = useLanguage();

  const privacyTranslations: Record<string, PolicyDocument> = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'Last updated: November 2025',
      sections: [
        {
          title: '1. Introduction',
          content: 'At Revomira, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.',
        },
        {
          title: '2. Information We Collect',
          content: 'We collect information that you provide directly to us, such as when you create an account, use our services, or contact us. This may include your name, email address, organization details, and any other information you choose to provide.',
        },
        {
          title: '3. How We Use Your Information',
          content: 'We use the information we collect to provide, maintain, and improve our services, process transactions, send you technical notices and support messages, and respond to your inquiries.',
        },
        {
          title: '4. Data Security',
          content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
        },
        {
          title: '5. Your Rights',
          content: 'You have the right to access, update, or delete your personal information at any time. You can do this through your account settings or by contacting us directly.',
        },
        {
          title: '6. Cookies',
          content: 'We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.',
        },
        {
          title: '7. Contact Us',
          content: 'If you have any questions about this Privacy Policy, please contact us at info@revomira.com.',
        },
      ],
    },
    tr: {
      title: 'Gizlilik Politikası',
      subtitle: 'Son güncelleme: Kasım 2025',
      sections: [
        {
          title: '1. Giriş',
          content: 'Revomira olarak, gizliliğinizi korumaya kararlıyız. Bu Gizlilik Politikası, hizmetimizi kullandığınızda bilgilerinizi nasıl topladığımızı, kullandığımızı, açıkladığımızı ve koruduğumuzu açıklar.',
        },
        {
          title: '2. Topladığımız Bilgiler',
          content: 'Hesap oluşturduğunuzda, hizmetlerimizi kullandığınızda veya bizimle iletişime geçtiğinizde doğrudan bize sağladığınız bilgileri topluyoruz. Bu, adınız, e-posta adresiniz, organizasyon detaylarınız ve sağlamayı seçtiğiniz diğer bilgileri içerebilir.',
        },
        {
          title: '3. Bilgilerinizi Nasıl Kullanıyoruz',
          content: 'Topladığımız bilgileri hizmetlerimizi sağlamak, sürdürmek ve iyileştirmek, işlemleri işlemek, size teknik bildirimler ve destek mesajları göndermek ve sorularınıza yanıt vermek için kullanıyoruz.',
        },
        {
          title: '4. Veri Güvenliği',
          content: 'Kişisel bilgilerinizi yetkisiz erişime, değişikliğe, ifşaya veya yok edilmeye karşı korumak için uygun teknik ve organizasyonel önlemler uyguluyoruz.',
        },
        {
          title: '5. Haklarınız',
          content: 'Kişisel bilgilerinize erişme, güncelleme veya silme hakkına sahipsiniz. Bunu hesap ayarlarınızdan veya doğrudan bizimle iletişime geçerek yapabilirsiniz.',
        },
        {
          title: '6. Çerezler',
          content: 'Hizmetimizdeki aktiviteyi takip etmek ve belirli bilgileri tutmak için çerezler ve benzeri izleme teknolojileri kullanıyoruz. Tarayıcınıza tüm çerezleri reddetmesini veya bir çerez gönderildiğinde bunu belirtmesini söyleyebilirsiniz.',
        },
        {
          title: '7. Bizimle İletişime Geçin',
          content: 'Bu Gizlilik Politikası hakkında herhangi bir sorunuz varsa, lütfen info@revomira.com adresinden bizimle iletişime geçin.',
        },
      ],
    },
  };

  const cookieTranslations: Record<string, PolicyDocument> = {
    en: {
      title: 'Cookie Policy',
      subtitle: 'Last updated: November 2025',
      sections: [
        {
          title: '1. What Are Cookies',
          content: 'Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to the website owners.',
        },
        {
          title: '2. How We Use Cookies',
          content: 'We use cookies to enhance your experience on our website, analyze site usage, and assist in our marketing efforts. We use both session cookies (which expire when you close your browser) and persistent cookies (which stay on your device until they expire or you delete them).',
        },
        {
          title: '3. Types of Cookies We Use',
          content: [
            {
              type: 'Essential Cookies',
              description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually set in response to actions made by you, such as setting your privacy preferences or logging in.',
            },
            {
              type: 'Analytics Cookies',
              description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the way our website works.',
            },
          ],
        },
        {
          title: '4. Managing Cookies',
          content: 'You can control and manage cookies in various ways. Most browsers allow you to refuse or accept cookies. You can also delete cookies that have already been set. However, please note that blocking or deleting cookies may impact your experience on our website.',
        },
        {
          title: '5. Third-Party Cookies',
          content: 'In addition to our own cookies, we may also use various third-party cookies to report usage statistics and deliver advertisements. These cookies are subject to the respective third parties\' privacy policies.',
        },
        {
          title: '6. Contact Us',
          content: 'If you have any questions about our use of cookies, please contact us at info@revomira.com.',
        },
      ],
    },
    tr: {
      title: 'Çerez Politikası',
      subtitle: 'Son güncelleme: Kasım 2025',
      sections: [
        {
          title: '1. Çerezler Nedir',
          content: 'Çerezler, bir web sitesini ziyaret ettiğinizde bilgisayarınıza veya mobil cihazınıza yerleştirilen küçük metin dosyalarıdır. Web sitelerinin daha verimli çalışmasını sağlamak ve web sitesi sahiplerine bilgi sağlamak için yaygın olarak kullanılırlar.',
        },
        {
          title: '2. Çerezleri Nasıl Kullanıyoruz',
          content: 'Web sitemizdeki deneyiminizi geliştirmek, site kullanımını analiz etmek ve pazarlama çabalarımıza yardımcı olmak için çerezler kullanıyoruz. Hem oturum çerezleri (tarayıcınızı kapattığınızda sona eren) hem de kalıcı çerezler (süresi dolana veya siz silene kadar cihazınızda kalan) kullanıyoruz.',
        },
        {
          title: '3. Kullandığımız Çerez Türleri',
          content: [
            {
              type: 'Temel Çerezler',
              description: 'Bu çerezler web sitesinin çalışması için gereklidir ve kapatılamaz. Genellikle gizlilik tercihlerinizi ayarlama veya giriş yapma gibi sizin yaptığınız işlemlere yanıt olarak ayarlanır.',
            },
            {
              type: 'Analitik Çerezler',
              description: 'Bu çerezler, ziyaretçilerin web sitemizle nasıl etkileşim kurduğunu anonim olarak bilgi toplayarak ve raporlayarak anlamamıza yardımcı olur. Bu, web sitemizin çalışma şeklini iyileştirmemize yardımcı olur.',
            },
          ],
        },
        {
          title: '4. Çerezleri Yönetme',
          content: 'Çerezleri çeşitli şekillerde kontrol edebilir ve yönetebilirsiniz. Çoğu tarayıcı çerezleri reddetmenize veya kabul etmenize izin verir. Ayrıca zaten ayarlanmış çerezleri silebilirsiniz. Ancak, çerezleri engellemenin veya silmenin web sitemizdeki deneyiminizi etkileyebileceğini lütfen unutmayın.',
        },
        {
          title: '5. Üçüncü Taraf Çerezleri',
          content: 'Kendi çerezlerimize ek olarak, kullanım istatistiklerini raporlamak ve reklamlar sunmak için çeşitli üçüncü taraf çerezleri de kullanabiliriz. Bu çerezler ilgili üçüncü tarafların gizlilik politikalarına tabidir.',
        },
        {
          title: '6. Bizimle İletişime Geçin',
          content: 'Çerez kullanımımız hakkında herhangi bir sorunuz varsa, lütfen info@revomira.com adresinden bizimle iletişime geçin.',
        },
      ],
    },
  };

  const kvkkTranslations: Record<string, PolicyDocument> = {
    en: {
      title: 'KVKK Disclosure and Explicit Consent',
      subtitle: 'Last updated: May 2026',
      sections: [
        {
          title: '1. Data Controller',
          content: 'Revomira acts as the data controller for personal data processed during registration and service usage in line with Turkish Personal Data Protection Law (KVKK No. 6698).',
        },
        {
          title: '2. Processed Data',
          content: 'We process your identity and contact data such as company name, contact person, email, phone, organization metadata and service usage records required for delivering the platform.',
        },
        {
          title: '3. Purpose of Processing',
          content: 'Your data is processed to create your account, provide subscription services, perform support operations, and fulfill legal obligations.',
        },
        {
          title: '4. Legal Basis and Consent',
          content: 'By checking the KVKK consent box, you provide explicit consent for processing your personal data for the above purposes. You may withdraw consent by contacting us, without affecting processing already carried out lawfully.',
        },
        {
          title: '5. Data Transfer and Retention',
          content: 'Data may be processed by contracted infrastructure/email/payment providers under confidentiality and security obligations. Retention periods follow legal and contractual requirements.',
        },
        {
          title: '6. Your Rights Under KVKK',
          content: 'You may request information on your processed data, correction, deletion, restriction, objection, and compensation rights under Article 11 of KVKK by contacting us.',
        },
      ],
    },
    tr: {
      title: 'KVKK Aydınlatma ve Açık Rıza Metni',
      subtitle: 'Son güncelleme: Mayıs 2026',
      sections: [
        {
          title: '1. Veri Sorumlusu',
          content: 'Revomira, kayıt ve hizmet kullanım sürecinde işlenen kişisel veriler bakımından 6698 sayılı KVKK kapsamında veri sorumlusudur.',
        },
        {
          title: '2. İşlenen Veri Kategorileri',
          content: 'Hizmetin sunulması için şirket adı, yetkili kişi, e-posta, telefon, organizasyon bilgileri ve kullanım kayıtları gibi kimlik ve iletişim verileri işlenir.',
        },
        {
          title: '3. İşleme Amacı',
          content: 'Verileriniz hesap oluşturma, abonelik hizmetinin sunulması, destek süreçleri ve yasal yükümlülüklerin yerine getirilmesi amaçlarıyla işlenir.',
        },
        {
          title: '4. Hukuki Sebep ve Açık Rıza',
          content: 'KVKK onay kutusunu işaretleyerek yukarıdaki amaçlarla kişisel verilerinizin işlenmesine açık rıza vermiş olursunuz. Talep halinde rızanızı geri alabilirsiniz; bu durum geri alma öncesi hukuka uygun işlemleri etkilemez.',
        },
        {
          title: '5. Veri Aktarımı ve Saklama',
          content: 'Veriler, gizlilik ve güvenlik yükümlülükleri altındaki altyapı/e-posta/ödeme hizmet sağlayıcılarıyla sınırlı olarak paylaşılabilir. Saklama süreleri yasal ve sözleşmesel yükümlülüklere göre belirlenir.',
        },
        {
          title: '6. KVKK Kapsamındaki Haklarınız',
          content: 'KVKK madde 11 kapsamında verilerinizin işlenip işlenmediğini öğrenme, düzeltme, silme, sınırlama, itiraz ve zarar giderim haklarınızı bizimle iletişime geçerek kullanabilirsiniz.',
        },
      ],
    },
  };

  const content = type === 'privacy'
    ? (privacyTranslations[language] || privacyTranslations.en)
    : type === 'cookie'
      ? (cookieTranslations[language] || cookieTranslations.en)
      : (kvkkTranslations[language] || kvkkTranslations.en);

  const Icon = type === 'cookie' ? Cookie : Shield;
  const iconColor = type === 'cookie'
    ? 'text-purple-600 dark:text-purple-400'
    : 'text-primary';
  const iconBg = type === 'cookie'
    ? 'bg-purple-100 dark:bg-purple-900/30'
    : 'bg-primary/15';

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <ModalHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${iconBg}`}>
              <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <div>
              <ModalTitle className="text-2xl font-bold text-foreground">
                {content.title}
              </ModalTitle>
              <ModalDescription className="text-sm text-muted-foreground mt-1">
                {content.subtitle}
              </ModalDescription>
            </div>
          </div>
        </ModalHeader>
        <div className="space-y-6 py-4">
          {content.sections.map((section, index) => (
            <div key={index} className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">
                {section.title}
              </h2>
              {Array.isArray(section.content) ? (
                <div className="space-y-4">
                  {(section.content as PolicyContentItem[]).map((item, itemIndex) => (
                    <Card key={itemIndex} className="border-border">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground mb-2">
                          {item.type}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {section.content}
                </p>
              )}
            </div>
          ))}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default PolicyModal;

